import { useEffect, useRef, useState } from 'react';
import { useCreateCoverAiRunMutation, useCreateCoverDesignMutation } from '../../../redux/api/fymAPI';
import type { CoverBankBackground, GetCoverEditorResponse } from '../../../redux/api/fymAPI.types';
import { getFullAPIFileURL } from '../../common/getFullAPIFileURL';
import type { FirstVersionNote } from './EditorNotices';
import { firstOpenPlan } from './firstOpen';

export interface FirstOpenResult {
  /** The version that the first open made. */
  designUuid: string;
  /** The AI run that it started, or null. */
  runUuid: string | null;
  note: FirstVersionNote;
}

interface UseFirstOpenProps {
  accountId: number;
  catalogItemUuid: string;
  /** The answer of the editor read; undefined while it loads. */
  data: GetCoverEditorResponse | undefined;
  /** The bank; undefined while it loads (an empty list when the read failed). */
  backgrounds: CoverBankBackground[] | undefined;
  /** False for the example artist: it gets a local first version, and nothing is saved. */
  enabled: boolean;
}

/**
 * The first open of a song with no design (owner decision D4: the editor never opens empty). Once
 * the song and the bank are read, it saves the first version (`firstOpenPlan`) and, with the welcome
 * run of the trial, starts that AI run. A paid plan starts its AI run with the button. A run that does not start leaves the bank picture; the
 * note then says that AI is not available now. It runs one time for each open of the editor.
 */
export function useFirstOpen({ accountId, catalogItemUuid, data, backgrounds, enabled }: UseFirstOpenProps) {
  const [createDesign] = useCreateCoverDesignMutation();
  const [createRun] = useCreateCoverAiRunMutation();
  const [result, setResult] = useState<FirstOpenResult | null>(null);
  const [isFailed, setIsFailed] = useState(false);
  const isStarted = useRef(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled || data === undefined || backgrounds === undefined || data.designs.length > 0 || isStarted.current) {
      return;
    }
    isStarted.current = true;
    const plan = firstOpenPlan({
      catalogItemUuid,
      song: data.song,
      backgrounds,
      plan: data.plan,
      quota: data.quota,
      welcomeRun: data.welcomeRun,
      toUrl: getFullAPIFileURL,
    });
    const run = async (): Promise<void> => {
      const { design } = await createDesign({ accountId, catalogItemUuid, doc: plan.doc }).unwrap();
      if (!plan.startAiRun) {
        setResult({ designUuid: design.uuid, runUuid: null, note: 'bank' });
        return;
      }
      try {
        const started = await createRun({ accountId, catalogItemUuid }).unwrap();
        setResult({ designUuid: design.uuid, runUuid: started.runUuid, note: 'aiMaking' });
      } catch (error) {
        console.warn('Cover editor: the first AI run did not start; the bank picture stays', error);
        setResult({ designUuid: design.uuid, runUuid: null, note: 'aiUnavailable' });
      }
    };
    run().catch((error: unknown) => {
      console.error('Cover editor: the first version was not saved', error);
      setIsFailed(true);
    });
  }, [accountId, attempt, backgrounds, catalogItemUuid, createDesign, createRun, data, enabled]);

  const retry = () => {
    isStarted.current = false;
    setIsFailed(false);
    setAttempt((count) => count + 1);
  };
  return { result, isFailed, retry };
}
